import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import BankIcon from '@/components/Icons/Bank';
import CaretRight from '@/components/Icons/CaretRight';
import CheckCircle from '@/components/Icons/CheckCircle';
import TaskEfficiencyIndicator from '@/components/TaskEfficiencyIndicator';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import useNoodleApi from '@/hooks/useNoodleApi';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import UserProfileContext from '@/layouts/UserProfileLayout/UserProfileContext';
import SkipTaskConfirmationModal from '@/modals/SkipTaskConfirmationModal';
import { useOpenBankStatementOrganizerPane } from '@/panes/bankStatementsOrganizerHooks';
import * as tsClient from '@/tsClient';
import {
  BankAccountIntegrationRequestStatus,
  type Creator,
  MESSAGE_ATTACHMENT_REFERENCE_TYPES,
} from '@/typings/api-models';

import s from './BankStatementsOrganizerCard.module.scss';

type Props = {
  creator: Pick<Creator, 'slug'>;
  request: { bankAccountIntegrationId: string };
  canClick: boolean;
  canSkip: boolean;
  setIsFulfilled?: (b: boolean) => void;
  setIsSkipped?: (b: boolean) => void;
  onChange?: () => Promise<void>;
  integrationSlugs?: string[];
  userWorkflowId?: string;
  assigneeIds?: string[];
  creatorSlug?: string;
  reload?: () => Promise<void>;
  status: BankAccountIntegrationRequestStatus;
};

const BankStatementsOrganizerCard: React.FC<Props> = ({
  creator,
  setIsFulfilled,
  setIsSkipped,
  canClick,
  canSkip,
  request,
  creatorSlug,
  reload,
  status,
}) => {
  const isSkipped = status === BankAccountIntegrationRequestStatus.SKIPPED;
  const isCompleted = status === BankAccountIntegrationRequestStatus.COMPLETED;
  const [skipTaskModalOpen, setSkipTaskModalOpen] = useState(false);
  const { isInDashboard } = useContext(DashboardContext);
  const { isInUserProfile } = useContext(UserProfileContext);
  const router = useRouter();
  const openBankStatementOrganizerPane = useOpenBankStatementOrganizerPane();

  const { getData: updateBankStatementOrganizerRequest } = useNoodleApi(
    tsClient.bankAccountIntegrations.updateBankStatementOrganizerRequest,
  );

  useEffect(() => {
    setIsSkipped?.(isSkipped);
  }, [isSkipped, setIsSkipped]);

  useEffect(() => {
    setIsFulfilled?.(isCompleted);
  }, [isCompleted, setIsFulfilled]);

  const handleClick = (): void => {
    if (isSkipped) {
      return;
    }
    if (isInDashboard || isInUserProfile) {
      openBankStatementOrganizerPane({ bankStatementOrganizerId: request.bankAccountIntegrationId });
    } else {
      void router.push(
        getUrl(IDENTIFIERS.BANK_STATEMENTS_ORGANIZER_DETAIL, {
          bankStatementOrganizerId: request.bankAccountIntegrationId,
          creatorSlug: creatorSlug || creator?.slug || (router.query.creatorSlug as string) || '',
        }),
      );
    }
  };

  const handleSkipTask = async (): Promise<void> => {
    await updateBankStatementOrganizerRequest({
      bankAccountIntegrationId: request.bankAccountIntegrationId,
      updates: {
        status: BankAccountIntegrationRequestStatus.SKIPPED,
      },
    });
    setSkipTaskModalOpen(false);
    if (reload) await reload();
  };

  const handleUnskipTask = async (): Promise<void> => {
    await updateBankStatementOrganizerRequest({
      bankAccountIntegrationId: request.bankAccountIntegrationId,
      updates: {
        skippedAt: null,
      },
    });
    if (reload) await reload();
  };

  return (
    <>
      {skipTaskModalOpen && (
        <SkipTaskConfirmationModal
          onClose={() => setSkipTaskModalOpen(false)}
          onConfirm={handleSkipTask}
          taskType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.BANK_ACCOUNT_INTEGRATION}
        />
      )}
      <Buttons
        key={request.bankAccountIntegrationId}
        className={s.container}
        isWrapper
        isFullWidth
        disabled={!canClick || (isSkipped && !canSkip)}
        style={isSkipped ? { backgroundColor: 'var(--color-gray-25)', borderRadius: 8 } : {}}
        onClick={handleClick}
      >
        {isCompleted ? (
          <CheckCircle size={16} weight='fill' color='var(--color-success)' />
        ) : (
          <figure>
            {isSkipped && <BankIcon size={20} color='var(--color-gray-100)' />}
            {!isSkipped && <BankIcon size={20} color='var(--color-primary)' />}
          </figure>
        )}
        <div>
          <div className={s.type}>
            <p
              className={isCompleted ? 'caption-bold' : 'body-sm-bold'}
              style={isCompleted ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
            >
              Upload Bank Statements
            </p>
          </div>
          {isSkipped && <p className='caption'>This task is no longer required.</p>}
        </div>
        {isSkipped && isInDashboard && (
          <Buttons
            isWrapper
            onClick={(e) => {
              e.stopPropagation();
              void handleUnskipTask();
            }}
          >
            <div className={s.open}>Unskip</div>
          </Buttons>
        )}
        {canClick && !isSkipped && (
          <>
            {isCompleted ? (
              <CaretRight size={12} color='var(--color-gray-100)' />
            ) : (
              <div
                className={s.open}
                {...(!isCompleted &&
                  !isInDashboard && {
                    style: {
                      backgroundColor: 'var(--color-primary)',
                      border: '1px solid var(--color-primary)',
                      color: 'var(--color-gray-0)',
                    },
                  })}
              >
                View
              </div>
            )}
          </>
        )}
      </Buttons>
      <TaskEfficiencyIndicator
        className='px-3 py-1'
        referenceType='bank_account_integration'
        referenceId={request.bankAccountIntegrationId}
        isSkipped={isSkipped}
      />
      {canSkip && !isSkipped && !isCompleted && (
        <Buttons
          onClick={(event) => {
            event.stopPropagation();
            setSkipTaskModalOpen(true);
          }}
          className={s.skip}
        >
          Skip
        </Buttons>
      )}
    </>
  );
};

export default BankStatementsOrganizerCard;
